/**
 * Per-player (member) statistics derived from the house's loaded games.
 *
 * The data model speaks of "members" and "transactions"; the UI speaks of
 * "players" and "sessions". This helper does that translation once so the
 * player index and the player detail view share a single computation.
 *
 * A game must be hydrated (its `players[].totals` and `players[].transactions`
 * present — i.e. loaded via `loadGames`) for it to contribute. Games that are
 * still just summary stubs are skipped.
 */

const BUY_TYPES = ["BUY_IN", "ADD_ON"];

/** Build the chronological session list for one member across all games. */
function collectSessions(memberId, games) {
    const sessions = [];

    Object.values(games || {}).forEach((game) => {
        const player = (game.players || []).find((p) => p.memberId === memberId);
        if (!player || !player.totals) return;

        const transactions = player.transactions || [];
        const unsettled = transactions.some((t) => t.isSettled === false);

        sessions.push({
            gameId: game.id,
            description: game.description,
            date: game.date,
            net: player.totals.net,
            buyIn: player.totals.buyIn,
            cashOut: player.totals.cashOut,
            unsettled,
            transactions,
        });
    });

    sessions.sort((a, b) => new Date(b.date) - new Date(a.date));
    return sessions;
}

/**
 * Net amount this player still owes the house (positive) or the house owes
 * the player (negative), summed from unsettled transactions: an unsettled
 * buy-in is money the player took on credit; an unsettled cash-out is money
 * the house hasn't paid out yet.
 */
function outstandingOwed(sessions) {
    let owed = 0;
    sessions.forEach((s) => {
        s.transactions.forEach((t) => {
            if (t.isSettled !== false) return;
            owed += BUY_TYPES.includes(t.type) ? t.amount : -t.amount;
        });
    });
    return owed;
}

/** Tally settled / total counts per payment method this player has used. */
function paymentBreakdown(sessions) {
    const byType = {};
    sessions.forEach((s) => {
        s.transactions.forEach((t) => {
            const type = t.paymentType || "CASH";
            const entry = byType[type] || (byType[type] = { type, total: 0, settled: 0 });
            entry.total += 1;
            if (t.isSettled !== false) entry.settled += 1;
        });
    });
    // Most-used first.
    return Object.values(byType).sort((a, b) => b.total - a.total);
}

/**
 * Compute the full stat bundle for a member. Returns zeroed/empty values for
 * a member who has never sat down, so callers don't need to special-case it.
 */
export function computeMemberStats(memberId, games) {
    const sessions = collectSessions(memberId, games);
    const played = sessions.length;

    let allTimeNet = 0;
    let buyInTotal = 0;
    let wins = 0;
    let bestNight = null;
    let worstNight = null;

    sessions.forEach((s) => {
        allTimeNet += s.net;
        buyInTotal += s.buyIn;
        if (s.net > 0) wins += 1;
        if (!bestNight || s.net > bestNight.net) bestNight = s;
        if (!worstNight || s.net < worstNight.net) worstNight = s;
    });

    return {
        sessions,
        gamesPlayed: played,
        allTimeNet,
        owed: outstandingOwed(sessions),
        winRate: played ? wins / played : 0,
        wins,
        avgBuyIn: played ? buyInTotal / played : 0,
        highestWin: bestNight && bestNight.net > 0 ? bestNight.net : 0,
        biggestLoss: worstNight && worstNight.net < 0 ? worstNight.net : 0,
        bestNight: bestNight && bestNight.net > 0 ? bestNight : null,
        worstNight: worstNight && worstNight.net < 0 ? worstNight : null,
        lastPlayed: sessions[0] || null,
        payments: paymentBreakdown(sessions),
    };
}

/** Two-letter initials for an avatar, from a member's display name. */
export function initialsOf(name = "") {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return "?";
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}
